import { useState, useEffect, useRef } from 'react';
import { Table, Loader, Group } from '@mantine/core';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../utils/date';
import Header from '../../components/util/header';
import { Language } from '../../languages/languageHandler';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import PaginationComponent from '../../components/pagination';
import { formatCurrAmount, getLocationName } from '../../utils/script';
import { ICurrency } from '../../../libraries/currencies';
import {
  fetchUserShiftReport,
  fetchUserShiftsList,
  fetchUserShiftsListXLS,
  UserShift
} from '../../../libraries/user-shift';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../context/status-oper-dialog';
import { IconFileTypeXls } from '@tabler/icons-react';
import './styles.scss';

const UserShifts = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const controller = useRef(new AbortController()).current;
  const [userShifts, setUserShifts] = useState<UserShift[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData | undefined>(undefined);
  const [offset, setOffset] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 1,
    minHeightForDynamic: 400
  });

  const { handleApiError } = useStatusOperDialog();

  // Initial load (no filters)
  useEffect(() => {
    getUserShiftList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getUserShiftList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, offset, filterBarData]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const getUserShiftList = () => {
    if (!filterBarData || !limit) {
      return;
    }
    setLoading(true);
    setUserShifts([]);
    setTotalRows(0);

    fetchUserShiftsList(
      controller,
      filterBarData.entity || 0,
      filterBarData.lid || 0,
      filterBarData.eid || 0,
      filterBarData.period || 0,
      offset,
      limit,
      true,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime),
      filterBarData.uid
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setUserShifts(response?.data?.list || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePage = (n: number) => {
    const newOffset = (n - 1) * limit;
    setCurrentPage(n);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getXLSListReport = async () => {
    if (!filterBarData) return;

    try {
      const response = await fetchUserShiftsListXLS(
        filterBarData.entity || 0,
        filterBarData.lid || 0,
        filterBarData.eid || 0,
        filterBarData.period || 0,
        controller,
        formatDateToISO(filterBarData.beginDateTime),
        formatDateToISO(filterBarData.endDateTime),
        filterBarData.uid
      );

      if (!isZarphSuccessResponse(response)) {
        handleApiError(response, lang.USER_SHIFTS_REPORT_ERROR);
      }
    } catch (err: any) {
      handleApiError(err, lang.USER_SHIFTS_REPORT_ERROR);
    }
  };

  const getXLSReport = async (row: UserShift) => {
    if (!filterBarData) return;

    try {
      const response = await fetchUserShiftReport(
        row.eid || 0,
        filterBarData.entity || 0,
        row.user?.uid || 0,
        row.shift || 0,
        controller
      );

      if (!isZarphSuccessResponse(response)) {
        handleApiError(response, lang.USER_SHIFTS_REPORT_ERROR);
      }
    } catch (err: any) {
      handleApiError(err, lang.USER_SHIFTS_REPORT_ERROR);
    }
  };

  return (
    <div className="user-shift-page">
      <Header pageTitle={lang.USER_SHIFT_TITLE} />

      <FilterBar
        onDataChange={handleFilterBarOnDataChange}
        onSearch={getUserShiftList}
        fieldsUsage={{
          entity: true,
          lid: true,
          eid: true,
          beginDateTime: true,
          endDateTime: true,
          period: true
        }}
        xlsReport={userShifts.length ? getXLSListReport : undefined}
      />

      <Table.ScrollContainer minWidth={500}>
        <Table striped verticalSpacing="sm" ref={tableRef as any}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>EID</Table.Th>
              <Table.Th>{lang.LOGIN_USER}</Table.Th>
              <Table.Th>{lang.ENTERPRISE_EQUIP_PERIODS_DT_OPEN}</Table.Th>
              <Table.Th>{lang.ENTERPRISE_EQUIP_PERIODS_DT_CLOSE}</Table.Th>
              <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>
              <Table.Th>{lang.USER_SHIFTS_METHODS_CASH}</Table.Th>
              <Table.Th>{lang.USER_SHIFTS_METHODS_CARD}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : !userShifts.length ? (
              <Table.Tr>
                <Table.Td colSpan={8} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              userShifts?.map((row, index) => {
                // Find the matching currency for this row
                const matchingCurrency = currencies?.find(c => c.cid === row?.total?.cid);

                return (
                  <Table.Tr key={index}>
                    <Table.Td>{getLocationName(row.sid)}</Table.Td>
                    <Table.Td>{row.eid}</Table.Td>
                    <Table.Td>{row.user ? `${row.user.uid} - ${row.user.name}` : '-'}</Table.Td>
                    <Table.Td>{formatDateTimeNSecLabel(row.dateTimeOpen, region)}</Table.Td>
                    <Table.Td>{formatDateTimeNSecLabel(row.dateTimeClose, region)}</Table.Td>
                    <Table.Td>
                      {matchingCurrency ? formatCurrAmount(row?.total?.total || 0, matchingCurrency) : '-'}
                    </Table.Td>
                    <Table.Td>
                      {matchingCurrency
                        ? formatCurrAmount((row?.total?.paid ?? 0) - (row?.total?.paidAP ?? 0), matchingCurrency)
                        : '-'}
                    </Table.Td>
                    <Table.Td>
                      {matchingCurrency ? formatCurrAmount(row?.total?.paidAP ?? 0, matchingCurrency) : '-'}
                    </Table.Td>
                    <Table.Td>
                      <IconFileTypeXls stroke={2} onClick={() => getXLSReport(row)} style={{ cursor: 'pointer' }} />
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default UserShifts;
